import { createSlice } from '@reduxjs/toolkit';
import MerchantService from '../../services/merchant';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  merchants: [],
  merchant: null,
  totalCount: 0,
  pagination: null
};

const slice = createSlice({
  name: 'merchant',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET MERCHANTS SUCCESS
    getMerchantsSuccess(state, action) {
      state.isLoading = false;
      state.merchants = action.payload.data || [];
      state.totalCount = action.payload.pagination?.total || 0;
      state.pagination = action.payload.pagination || null;
    },

    // GET MERCHANT SUCCESS
    getMerchantSuccess(state, action) {
      state.isLoading = false;
      state.merchant = action.payload.data || null;
    },

    // CREATE MERCHANT SUCCESS
    createMerchantSuccess(state, action) {
      state.isLoading = false;
      const newMerchant = action.payload.data;
      if (newMerchant) {
        state.merchants.unshift(newMerchant);
        state.totalCount += 1;
      }
    },

    // UPDATE MERCHANT SUCCESS
    updateMerchantSuccess(state, action) {
      state.isLoading = false;
      const updatedMerchant = action.payload.data;
      if (updatedMerchant) {
        const index = state.merchants.findIndex((merchant) => merchant.id === updatedMerchant.id);
        if (index !== -1) {
          state.merchants[index] = updatedMerchant;
        }
        if (state.merchant && state.merchant.id === updatedMerchant.id) {
          state.merchant = updatedMerchant;
        }
      }
    },

    // DELETE MERCHANT SUCCESS
    deleteMerchantSuccess(state, action) {
      state.isLoading = false;
      const deletedId = action.payload;
      state.merchants = state.merchants.filter((merchant) => merchant.id !== deletedId);
      state.totalCount = Math.max(0, state.totalCount - 1);
      if (state.merchant && state.merchant.id === deletedId) {
        state.merchant = null;
      }
    },

    // CLEAR MERCHANT
    clearMerchant(state) {
      state.merchant = null;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  hasError,
  getMerchantsSuccess,
  getMerchantSuccess,
  createMerchantSuccess,
  updateMerchantSuccess,
  deleteMerchantSuccess,
  clearMerchant
} = slice.actions;

// ----------------------------------------------------------------------

export function getMerchants(params = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await MerchantService.getList(params);
      if (response.success) {
        dispatch(slice.actions.getMerchantsSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to fetch merchants'));
      }
    } catch (error) {
      console.error('Get merchants error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch merchants'));
    }
  };
}

export function getMerchant(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await MerchantService.getById(id);
      if (response.success) {
        dispatch(slice.actions.getMerchantSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to fetch merchant'));
      }
    } catch (error) {
      console.error('Get merchant error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch merchant'));
    }
  };
}

export function createMerchant(merchantData) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await MerchantService.create(merchantData);
      if (response.success) {
        dispatch(slice.actions.createMerchantSuccess(response));
        return { success: true, data: response.data };
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to create merchant'));
        return { success: false, errors: response.errors };
      }
    } catch (error) {
      console.error('Create merchant error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to create merchant'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function updateMerchant(id, merchantData) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await MerchantService.update(id, merchantData);
      if (response.success) {
        dispatch(slice.actions.updateMerchantSuccess(response));
        return { success: true, data: response.data };
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to update merchant'));
        return { success: false, errors: response.errors };
      }
    } catch (error) {
      console.error('Update merchant error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to update merchant'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function deleteMerchant(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await MerchantService.delete(id);
      if (response.success) {
        dispatch(slice.actions.deleteMerchantSuccess(id));
        return { success: true };
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to delete merchant'));
        return { success: false, errors: response.errors };
      }
    } catch (error) {
      console.error('Delete merchant error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to delete merchant'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function updateMerchantPreferences(merchantId, preferences) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await MerchantService.updatePreferences(merchantId, preferences);
      if (response.success) {
        // Refresh the merchant data to get updated preferences
        dispatch(getMerchant(merchantId));
        return { success: true, data: response.data };
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to update preferences'));
        return { success: false, errors: response.errors };
      }
    } catch (error) {
      console.error('Update merchant preferences error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to update preferences'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}
